import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { addMonths, format, getMonth, getYear } from "date-fns";
import { de, enUS } from "date-fns/locale";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { AuthContext } from "../../../AuthContext";
import { isEmployeeAccessLevel } from "../../../utils/accessLevels";
import DownloadBuilder from "../../../components/DownloadBuilder";
import useToast from "../../../components/useToast";
import { API_BASE_URL } from "../../../config";
import AppButton from "../../../design-system/components/AppButton";
import { AppStatusChip } from "../../../design-system/components/AppChip";
import AppPeriodNavigator from "../../../design-system/components/AppPeriodNavigator";
import AppSelect from "../../../design-system/components/AppSelect";
import AppTable from "../../../design-system/components/AppTable";
import AppTablePane from "../../../design-system/components/AppTablePane";
import AppText from "../../../design-system/components/AppText";
import AppToolbar from "../../../design-system/components/AppToolbar";
import AppTooltip from "../../../design-system/components/AppTooltip";
import AppWorkspace from "../../../design-system/components/AppWorkspace";
import {
  IconBlock,
  IconChevronLeft,
  IconChevronRight,
  IconHoliday,
  IconImport,
  IconSuccess,
  IconTimetable,
  IconWarning,
} from "../../../design-system/icons";
import AvailabilityDateField from "../../availability/AvailabilityDateField";
import { setActivePeriod } from "../../shiftplan_v2/api/period";
import { getEmployeeShiftplan, getShiftplanBootstrap } from "../../shiftplan_v2/api/shiftplanApi";
import { mapSchedulerPayload } from "../../shiftplan_v2/api/mappers";
import { getShiftDayKeys } from "../../shiftplan_v2/domain/model/selectors";
import { getUserHolidayDayKeys } from "../../shiftplan_v2/domain/model/userHolidays";
import {
  getAbsencePlannedMinutesPerDay,
  getEmployeeDailyTargetHours,
  getHolidayPlannedMinutesPerDay,
  getShiftPlannedMinutes,
  holidayCounts,
  isWorkingDay,
} from "../../shiftplan_v2/domain/model/workload";
import "../reporting.css";

const ALL_EMPLOYEES_VALUE = "__all_employees__";
const ADMIN_ACCESS_LEVELS = ["Super_Admin", "Admin", "company_admin"];
const TOAST_OPTIONS = {};
const DEFAULT_WORKING_DAYS_PER_WEEK = 5;

const EMPTY_DATA = {
  employees: [],
  shifts: [],
  departments: [],
  absenceOptions: [],
  userHolidays: [],
  publicHolidays: [],
  meta: {},
};

function parseDateTime(value) {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/*
  Dezimalstunden wie im Dashboard und in der Periodenleiste des Schichtplans:
  ganze Stunden ohne Nachkommastelle, sonst eine. Dieselbe Zahl darf nicht auf
  einer Seite als 143.4 und auf der anderen als 143:24 erscheinen.
*/
function formatHours(minutes, numberLocale) {
  if (minutes === null || minutes === undefined) return "–";
  const hours = Math.max(0, Math.round(Number(minutes) || 0)) / 60;
  const formatted = new Intl.NumberFormat(numberLocale, {
    maximumFractionDigits: Number.isInteger(hours) ? 0 : 1,
  }).format(hours);
  return `${formatted}h`;
}

function isWithinMonth(startValue, endValue, monthStart, monthEnd) {
  const start = parseDateTime(startValue);
  const end = parseDateTime(endValue || startValue);
  if (!start || !end) return false;
  return start <= monthEnd && end >= monthStart;
}

function isDateKeyInMonth(dateKey, monthStart, monthEnd) {
  const date = parseDateTime(`${dateKey}T00:00:00`);
  return Boolean(date) && date >= monthStart && date <= monthEnd;
}

/*
  Arbeitstage eines mehrtägigen Eintrags, die in den angezeigten Monat fallen.
  Absenzen und Ferien werden tageweise angerechnet — eine Ferienwoche zählt
  fünf Tage, nicht sieben. Dieselbe Regel wie in databases/hours_resolution.py.
*/
function monthWorkingDayKeys(dayKeys, monthStart, monthEnd, workingDaysPerWeek) {
  return (Array.isArray(dayKeys) ? dayKeys : []).filter((dateKey) => {
    if (!isDateKeyInMonth(dateKey, monthStart, monthEnd)) return false;
    const date = parseDateTime(`${dateKey}T00:00:00`);
    return Boolean(date) && isWorkingDay(date, workingDaysPerWeek);
  });
}

export default function ShiftplanReporting() {
  const { user } = useContext(AuthContext);
  const isEmployeeView = isEmployeeAccessLevel(user?.access_level);
  const { t, i18n } = useTranslation();
  const { error } = useToast(TOAST_OPTIONS);
  const [data, setData] = useState(EMPTY_DATA);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => getMonth(new Date()));
  const [selectedYear, setSelectedYear] = useState(() => getYear(new Date()));
  const [isLoading, setIsLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportPreviewLoading, setIsExportPreviewLoading] = useState(false);
  const [exportPreview, setExportPreview] = useState(null);
  const [exportTimeframe, setExportTimeframe] = useState({});
  const [exportTabMode, setExportTabMode] = useState("none");
  const [exportFilters, setExportFilters] = useState({});
  const [exportResetKey, setExportResetKey] = useState(0);
  const tablePaneRef = useRef(null);
  const [tablePaneWidth, setTablePaneWidth] = useState(null);
  const token = localStorage.getItem("session_token");

  const locale = useMemo(
    () => (String(i18n.language || "").toLowerCase().startsWith("de") ? de : enUS),
    [i18n.language]
  );
  // Zahlenformat wie im Dashboard: Deutsch auf de-CH, damit 143.4 mit Punkt
  // steht und nicht mit Komma.
  const numberLocale = useMemo(
    () =>
      String(i18n.language || "de").toLowerCase().startsWith("de")
        ? "de-CH"
        : i18n.language || "de-CH",
    [i18n.language]
  );
  const isAdmin = ADMIN_ACCESS_LEVELS.includes(data.meta?.accessLevel);
  const selectedPeriodDate = useMemo(
    () => new Date(selectedYear, selectedMonth, 1),
    [selectedMonth, selectedYear]
  );
  const monthStart = useMemo(
    () => new Date(selectedYear, selectedMonth, 1, 0, 0, 0, 0),
    [selectedMonth, selectedYear]
  );
  const monthEnd = useMemo(
    () => new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59, 999),
    [selectedMonth, selectedYear]
  );

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    try {
      // Ohne Zeitfenster liefert der Server den laufenden Monat — beim
      // Blättern bliebe die Tabelle sonst leer.
      setActivePeriod({
        startDate: format(monthStart, "yyyy-MM-dd"),
        endDate: format(monthEnd, "yyyy-MM-dd"),
      });
      // Mitarbeitende bekommen den Planungs-Bootstrap nicht (Entwürfe, Daten
      // aller Kolleginnen und Kollegen) - ihr Report rechnet auf dem
      // freigegebenen Plan der Mitarbeitenden-Ansicht.
      const payload = isEmployeeView ? await getEmployeeShiftplan() : await getShiftplanBootstrap();
      setData(mapSchedulerPayload(payload));
    } catch (requestError) {
      console.error("Shiftplan reporting could not be loaded:", requestError);
      error(t("notification.error"));
      setData(EMPTY_DATA);
    } finally {
      setIsLoading(false);
    }
  }, [error, isEmployeeView, monthEnd, monthStart, t]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const availableEmployees = useMemo(() => {
    if (isAdmin) return data.employees;
    const currentEmail = String(user?.email || "").toLowerCase();
    return data.employees.filter(
      (employee) => String(employee.email || "").toLowerCase() === currentEmail
    );
  }, [data.employees, isAdmin, user?.email]);

  const employeesById = useMemo(
    () => new Map(data.employees.map((employee) => [String(employee.id), employee])),
    [data.employees]
  );
  const selectedEmployeeId = isAdmin
    ? selectedUserId
    : String(availableEmployees[0]?.id || "");

  const workingDaysPerWeek = data.meta?.workingDays || DEFAULT_WORKING_DAYS_PER_WEEK;
  const vacationAbsenceIds = useMemo(
    () =>
      new Set(
        (data.absenceOptions || [])
          .filter((option) => option.isVacation)
          .map((option) => String(option.id))
      ),
    [data.absenceOptions]
  );

  /*
    Tage mit genehmigter Ferienbuchung. Eine Alt-Absenz "Ferien" am selben Tag
    zählt dann nicht mehr mit, sonst stünde dieselbe Person doppelt im Total.
  */
  const holidayDayKeys = useMemo(
    () =>
      new Set(
        (data.userHolidays || [])
          .filter(holidayCounts)
          .flatMap((holiday) =>
            monthWorkingDayKeys(
              getUserHolidayDayKeys(holiday),
              monthStart,
              monthEnd,
              workingDaysPerWeek
            ).map((dateKey) => `${holiday.userId}::${dateKey}`)
          )
      ),
    [data.userHolidays, monthEnd, monthStart, workingDaysPerWeek]
  );

  const rows = useMemo(() => {
    const employeeAllowed = (employeeId) =>
      !selectedEmployeeId || String(employeeId) === String(selectedEmployeeId);
    const dailyTargetHours = (employeeId) =>
      getEmployeeDailyTargetHours(employeesById.get(String(employeeId)), workingDaysPerWeek);

    const shiftRows = data.shifts
      .filter((entry) => employeeAllowed(entry.employeeId))
      .filter((entry) =>
        entry.entryType === "absence"
          // Absenzen zählen tageweise und können in zwei Monate ragen.
          ? isWithinMonth(entry.start, entry.end, monthStart, monthEnd)
          // Eine Schicht zählt vollständig in dem Monat, in dem sie beginnt.
          : isDateKeyInMonth(entry.dateKey, monthStart, monthEnd)
      )
      .map((entry) => {
        const employee = employeesById.get(String(entry.employeeId));
        const start = parseDateTime(entry.start);
        const end = parseDateTime(entry.end);
        let minutes;
        if (entry.entryType === "absence") {
          const skipHolidayDays = vacationAbsenceIds.has(String(entry.absenceId || ""));
          const dayKeys = monthWorkingDayKeys(
            getShiftDayKeys(entry),
            monthStart,
            monthEnd,
            workingDaysPerWeek
          ).filter(
            (dateKey) => !skipHolidayDays || !holidayDayKeys.has(`${entry.employeeId}::${dateKey}`)
          );
          minutes =
            dayKeys.length *
            getAbsencePlannedMinutesPerDay(entry, dailyTargetHours(entry.employeeId));
        } else {
          minutes = getShiftPlannedMinutes(entry);
        }
        return {
          id: `timetable-${entry.id}`,
          employee_id: String(entry.employeeId),
          first_name: employee?.firstName || "",
          last_name: employee?.lastName || "",
          name: employee?.name || entry.employeeName || "–",
          date: start,
          weekday: start ? format(start, "EEE", { locale }) : "–",
          type_key: entry.entryType === "absence" ? "absence" : "shift",
          type_label: entry.entryType === "absence"
            ? entry.title || t("shiftplan_report.types.absence")
            : t("shiftplan_report.types.shift"),
          shift_name: entry.title || "–",
          department: entry.department || "–",
          timeframe: entry.isAllDay
            ? t("shiftplan_report.full_day")
            : [start, end].every(Boolean)
              ? `${format(start, "HH:mm")}–${format(end, "HH:mm")}`
              : "–",
          planned_minutes: minutes,
          planned_hours: formatHours(minutes, numberLocale),
          release_status: entry.isDraft ? "draft" : "released",
        };
      });

    const holidayRows = data.userHolidays.flatMap((holiday) => {
      if (!holidayCounts(holiday) || !employeeAllowed(holiday.userId)) return [];
      const employee = employeesById.get(String(holiday.userId));
      const minutes = getHolidayPlannedMinutesPerDay(
        holiday,
        workingDaysPerWeek,
        dailyTargetHours(holiday.userId)
      );
      return monthWorkingDayKeys(
        getUserHolidayDayKeys(holiday),
        monthStart,
        monthEnd,
        workingDaysPerWeek
      )
        .map((dayKey) => new Date(`${dayKey}T00:00:00`))
        .map((date) => {
          return {
            id: `holiday-${holiday.id}-${format(date, "yyyy-MM-dd")}`,
            employee_id: String(holiday.userId),
            first_name: employee?.firstName || "",
            last_name: employee?.lastName || "",
            name: employee?.name || "–",
            date,
            weekday: format(date, "EEE", { locale }),
            type_key: "holiday",
            type_label: t("shiftplan_report.types.holiday"),
            shift_name: "",
            department: "–",
            timeframe: holiday.holidayTypeName || t("shiftplan_report.types.holiday"),
            planned_minutes: minutes,
            planned_hours: formatHours(minutes, numberLocale),
            release_status: "released",
          };
        });
    });

    return [...shiftRows, ...holidayRows].sort((left, right) => {
      const dateCompare = (left.date?.getTime() || 0) - (right.date?.getTime() || 0);
      if (dateCompare !== 0) return dateCompare;
      return left.name.localeCompare(right.name, i18n.language || "de");
    });
  }, [
    data.shifts,
    data.userHolidays,
    employeesById,
    holidayDayKeys,
    i18n.language,
    locale,
    monthEnd,
    monthStart,
    numberLocale,
    selectedEmployeeId,
    vacationAbsenceIds,
    workingDaysPerWeek,
    t,
  ]);

  const totalMinutes = useMemo(
    () => rows.reduce((total, row) => total + Math.max(0, Number(row.planned_minutes) || 0), 0),
    [rows]
  );

  /*
    Die Beschriftung kommt bewusst von hier statt aus formatPeriodTitle des DS:
    diese Seite ist zweisprachig, formatPeriodTitle formatiert fest auf de-CH.
    AppPeriodNavigator sieht periodLabel dafür vor.
  */
  const periodLabel = useMemo(
    () => format(selectedPeriodDate, "LLLL yyyy", { locale }),
    [locale, selectedPeriodDate]
  );

  const selectReportPeriod = (date) => {
    setSelectedMonth(getMonth(date));
    setSelectedYear(getYear(date));
  };

  const stepReportPeriod = (offset) => selectReportPeriod(addMonths(selectedPeriodDate, offset));

  const currentExportMonth = () =>
    `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;

  const exportHeaders = () => {
    const selectedLocationId = localStorage.getItem("selected_location_id");
    return {
      Authorization: `Bearer ${token}`,
      ...(selectedLocationId ? { "X-TimeTab-Location-ID": selectedLocationId } : {}),
    };
  };

  const buildExportPreviewParams = (
    nextTimeframe,
    tabMode = exportTabMode,
    filters = exportFilters
  ) => {
    const filterParams = { filters: JSON.stringify(filters || {}) };
    if (nextTimeframe?.mode === "range") {
      if (
        !nextTimeframe.start_date ||
        !nextTimeframe.end_date ||
        nextTimeframe.end_date < nextTimeframe.start_date
      ) {
        return null;
      }
      return {
        ...filterParams,
        start_date: nextTimeframe.start_date,
        end_date: nextTimeframe.end_date,
        tab_mode: tabMode,
      };
    }
    return {
      ...filterParams,
      month: nextTimeframe?.month || currentExportMonth(),
      tab_mode: tabMode,
    };
  };

  const loadExportPreview = async (
    nextTimeframe,
    tabMode = exportTabMode,
    filters = exportFilters
  ) => {
    const params = buildExportPreviewParams(nextTimeframe, tabMode, filters);
    if (!params) return null;
    setIsExportPreviewLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/web/shiftplan-reporting/export/preview`,
        { params, headers: exportHeaders() }
      );
      setExportPreview(response.data);
      return response.data;
    } catch (requestError) {
      console.error("Shiftplan export preview could not be loaded:", requestError);
      error(t("notification.error"));
      return null;
    } finally {
      setIsExportPreviewLoading(false);
    }
  };

  const openExportBuilder = async () => {
    const nextTimeframe = {
      mode: "month",
      month: currentExportMonth(),
      start_date: "",
      end_date: "",
    };
    setExportPreview(null);
    setExportTimeframe(nextTimeframe);
    setExportTabMode("none");
    setExportFilters({});
    setExportOpen(true);
    const preview = await loadExportPreview(nextTimeframe, "none", {});
    if (preview) setExportResetKey((current) => current + 1);
  };

  const handleExportTimeframeChange = (nextTimeframe) => {
    setExportTimeframe(nextTimeframe);
    loadExportPreview(nextTimeframe, exportTabMode, exportFilters).catch(() => {});
  };

  const handleExportFiltersChange = (nextFilters) => {
    setExportFilters(nextFilters);
    loadExportPreview(exportTimeframe, exportTabMode, nextFilters).catch(() => {});
  };

  const handleReportDownload = async ({
    format: exportFormat = "excel",
    columns: exportColumns,
    tab_mode: tabMode,
    filters,
    timeframe: selectedExportTimeframe,
    settings,
  }) => {
    if (!buildExportPreviewParams(selectedExportTimeframe)) return;
    const isPdfExport = exportFormat === "pdf";
    const extension = isPdfExport ? "pdf" : "xlsx";
    setIsExporting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/web/shiftplan-reporting/export`,
        {
          format: exportFormat,
          columns: exportColumns,
          tab_mode: tabMode,
          filters,
          timeframe: selectedExportTimeframe,
          settings,
        },
        {
          headers: exportHeaders(),
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: isPdfExport
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      const period = selectedExportTimeframe?.mode === "range"
        ? `${selectedExportTimeframe.start_date}_bis_${selectedExportTimeframe.end_date}`
        : selectedExportTimeframe?.month || currentExportMonth();
      link.setAttribute("download", `Schichtplanbericht_${period}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (requestError) {
      console.error("Shiftplan report download could not be created:", requestError);
      error(t("notification.error"));
    } finally {
      setIsExporting(false);
    }
  };

  const employeeOptions = useMemo(
    () => [
      { value: ALL_EMPLOYEES_VALUE, label: t("report.all_employees") },
      ...availableEmployees.map((employee) => ({
        value: String(employee.id),
        label: employee.name,
      })),
    ],
    [availableEmployees, t]
  );
  const selectedEmployeeValue = selectedUserId || ALL_EMPLOYEES_VALUE;
  const selectedEmployeeIndex = employeeOptions.findIndex(
    (option) => option.value === selectedEmployeeValue
  );
  const selectEmployeeAt = (index) => {
    const option = employeeOptions[index];
    if (!option) return;
    setSelectedUserId(option.value === ALL_EMPLOYEES_VALUE ? "" : option.value);
  };

  const showEmployeeName = isAdmin && !selectedUserId;
  const mergeEmployeeName =
    showEmployeeName && tablePaneWidth !== null && tablePaneWidth < 1400;
  const mergeDateWeekday = tablePaneWidth !== null && tablePaneWidth < 1250;
  const mergeDepartmentShift = tablePaneWidth !== null && tablePaneWidth < 1100;

  useEffect(() => {
    if (isLoading) return undefined;
    const pane = tablePaneRef.current;
    if (!pane) return undefined;
    const measure = () => {
      const width = Math.round(pane.getBoundingClientRect().width);
      setTablePaneWidth((current) => (current === width ? current : width));
    };
    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(measure);
    observer?.observe(pane);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [isLoading]);

  const columns = [
    showEmployeeName && !mergeEmployeeName && {
      key: "first_name",
      label: t("report.columns.first_name"),
      minWidth: 140,
      grow: 1,
      render: (value) => <AppText variant="heading-4" truncate>{value || "–"}</AppText>,
    },
    showEmployeeName && !mergeEmployeeName && {
      key: "last_name",
      label: t("report.columns.last_name"),
      minWidth: 150,
      grow: 1,
      render: (value) => <AppText variant="heading-4" truncate>{value || "–"}</AppText>,
    },
    showEmployeeName && mergeEmployeeName && {
      key: "name",
      label: t("report.columns.name"),
      minWidth: 200,
      grow: 1.4,
      render: (value) => <AppText variant="heading-4" truncate>{value || "–"}</AppText>,
    },
    {
      key: "date",
      label: t("date"),
      minWidth: mergeDateWeekday ? 154 : 118,
      grow: 0.8,
      sortAccessor: (row) => row.date?.getTime() || 0,
      render: (value) => (
        <AppText variant="body-1">
          {value
            ? format(value, mergeDateWeekday ? "EEE, dd.MM.yyyy" : "dd.MM.yyyy", { locale })
            : "–"}
        </AppText>
      ),
    },
    !mergeDateWeekday && {
      key: "weekday",
      label: t("report.columns.weekday"),
      minWidth: 90,
      grow: 0.6,
    },
    {
      key: "type_label",
      label: t("report.columns.working_type"),
      minWidth: 130,
      grow: 0.8,
      render: (_value, row) => {
        if (row.type_key === "holiday") {
          return (
            <AppStatusChip
              status="info"
              size="sm"
              leadingIcon={<IconHoliday />}
              className="reporting-type-chip reporting-type-chip--holiday"
            >
              {row.type_label}
            </AppStatusChip>
          );
        }
        if (row.type_key === "absence") {
          return (
            <AppStatusChip
              status="error"
              size="sm"
              leadingIcon={<IconBlock />}
              className="reporting-type-chip reporting-type-chip--absence"
            >
              {row.type_label}
            </AppStatusChip>
          );
        }
        return (
          <AppStatusChip status="info" size="sm" leadingIcon={<IconTimetable />}>
            {row.type_label}
          </AppStatusChip>
        );
      },
    },
    !mergeDepartmentShift && {
      key: "department",
      label: t("shiftplan_report.columns.department"),
      minWidth: 140,
      grow: 1,
    },
    !mergeDepartmentShift && {
      key: "shift_name",
      label: t("shiftplan_report.columns.shift"),
      minWidth: 150,
      grow: 1,
    },
    mergeDepartmentShift && {
      key: "department_shift",
      label: t("shiftplan_report.columns.department_shift"),
      minWidth: 190,
      grow: 1.3,
      sortAccessor: (row) => `${row.department || ""} ${row.shift_name || ""}`,
      render: (_value, row) => (
        <AppText variant="body-1" truncate>
          {[row.department, row.shift_name]
            .filter((value) => value && value !== "–")
            .join(" · ") || "–"}
        </AppText>
      ),
    },
    {
      key: "timeframe",
      label: t("report.timeframe"),
      minWidth: 128,
      grow: 0.8,
    },
    {
      key: "planned_hours",
      label: t("shiftplan_report.columns.planned_hours"),
      minWidth: 128,
      grow: 0.8,
    },
    {
      key: "release_status",
      label: t("shiftplan_report.columns.status"),
      minWidth: 72,
      grow: 0.4,
      render: (value) => {
        const label = value === "released"
          ? t("shiftplan_report.status.released")
          : t("shiftplan_report.status.draft");
        const StatusIcon = value === "released" ? IconSuccess : IconWarning;
        return (
          <AppTooltip content={label}>
            <StatusIcon
              aria-label={label}
              sx={{
                color: value === "released"
                  ? "var(--ds-color-success-default)"
                  : "var(--ds-color-warning-default)",
                fontSize: 18,
              }}
            />
          </AppTooltip>
        );
      },
    },
  ].filter(Boolean);

  return (
    <div className="reporting-page">
      <AppWorkspace title={t("shiftplan_report.title")} loading={isLoading}>
        <AppToolbar
          className="reporting-toolbar"
          left={(
            <>
              <div className="reporting-toolbar__period">
                {/* Dieselbe Navigation wie auf /availability, /demand und /shiftplan. */}
                <AppPeriodNavigator
                  viewMode="month"
                  referenceDate={selectedPeriodDate}
                  periodLabel={periodLabel}
                  size="md"
                  className="reporting-toolbar__period-navigator"
                  previousLabel={t("availability.previous_month")}
                  nextLabel={t("availability.next_month")}
                  onPreviousPeriod={() => stepReportPeriod(-1)}
                  onNextPeriod={() => stepReportPeriod(1)}
                  onSelectDate={selectReportPeriod}
                />
              </div>
              <AppTooltip content={t("report.download")}>
                <AppButton
                  variant="neutral"
                  size="md"
                  iconOnly
                  aria-label={t("report.download")}
                  onClick={openExportBuilder}
                  disabled={isExporting || isExportPreviewLoading}
                >
                  <IconImport />
                </AppButton>
              </AppTooltip>
            </>
          )}
          right={isAdmin ? (
            <div className="reporting-toolbar__employee-navigation">
              <AppTooltip content={t("team.previous_user")}>
                <AppButton
                  variant="neutral"
                  size="sm"
                  iconOnly
                  aria-label={t("team.previous_user")}
                  onClick={() => selectEmployeeAt(selectedEmployeeIndex - 1)}
                  disabled={selectedEmployeeIndex <= 0}
                >
                  <IconChevronLeft />
                </AppButton>
              </AppTooltip>
              <div className="reporting-toolbar__user">
                <AppSelect
                  size="md"
                  value={selectedEmployeeValue}
                  options={employeeOptions}
                  onChange={(event) => setSelectedUserId(
                    event.target.value === ALL_EMPLOYEES_VALUE ? "" : event.target.value
                  )}
                  searchable
                  searchPlaceholder={t("team.select_user")}
                  aria-label={t("team.select_user")}
                />
              </div>
              <AppTooltip content={t("team.next_user")}>
                <AppButton
                  variant="neutral"
                  size="sm"
                  iconOnly
                  aria-label={t("team.next_user")}
                  onClick={() => selectEmployeeAt(selectedEmployeeIndex + 1)}
                  disabled={
                    selectedEmployeeIndex < 0 ||
                    selectedEmployeeIndex >= employeeOptions.length - 1
                  }
                >
                  <IconChevronRight />
                </AppButton>
              </AppTooltip>
            </div>
          ) : null}
        />

        <AppTablePane ref={tablePaneRef} className="reporting-table-pane">
          <AppTable
            className="reporting-table"
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            emptyState={t("shiftplan_report.no_rows")}
            footer={(
              <>
                <AppTable.FooterMeta>
                  <span>
                    {t("shiftplan_report.total_planned_hours")}: {formatHours(totalMinutes, numberLocale)}
                  </span>
                </AppTable.FooterMeta>
                <AppTable.FooterSummary>
                  <span>{t("report.entries", { count: rows.length })}</span>
                </AppTable.FooterSummary>
              </>
            )}
          />
        </AppTablePane>
      </AppWorkspace>

      <DownloadBuilder
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={t("download_builder.title")}
        documentTitle={t("shiftplan_report.title")}
        description={`${exportPreview?.period_label || currentExportMonth()} · ${exportPreview?.scope_label || ""}`}
        columns={exportPreview?.columns || []}
        defaultColumnIds={exportPreview?.default_column_ids || []}
        tabModes={exportPreview?.tab_modes || []}
        defaultTabMode={exportPreview?.default_tab_mode || "none"}
        timeframeOptions={[
          { id: "month", label: t("availability.month") },
          { id: "range", label: t("availability.custom") },
        ]}
        defaultTimeframe={exportTimeframe}
        onTimeframeChange={handleExportTimeframeChange}
        filterGroups={exportPreview?.filter_groups || []}
        defaultFilters={exportPreview?.default_filters || {}}
        onFiltersChange={handleExportFiltersChange}
        onTabModeChange={(tabMode) => {
          setExportTabMode(tabMode);
          loadExportPreview(exportTimeframe, tabMode, exportFilters).catch(() => {});
        }}
        resetKey={exportResetKey}
        previewRows={exportPreview?.preview_rows || []}
        previewSections={exportPreview?.preview_sections || []}
        previewLoading={isExportPreviewLoading}
        isDownloading={isExporting}
        onDownload={handleReportDownload}
        dateFieldComponent={AvailabilityDateField}
        pdfEnabled
      />
    </div>
  );
}
